import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/i18n/locales";

/**
 * Localized blog content lives in `blog_post_translations` (one row per
 * post + locale). English is the base content stored on `blog_posts`.
 * These helpers fetch a translation and merge it over the base post so
 * the UI always has complete fields, falling back to English when a
 * translation is missing.
 */

export type BlogTranslation = {
  post_id: string;
  title: string;
  excerpt: string;
  content: string;
  meta_title: string;
  meta_description: string;
};

type LooseClient = {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (col: string, val: string) => {
        in: (col: string, vals: string[]) => Promise<{ data: BlogTranslation[] | null }>;
        eq: (col: string, val: string) => {
          maybeSingle: () => Promise<{ data: BlogTranslation | null }>;
        };
      };
    };
  };
};

const loose = supabase as unknown as LooseClient;

/** Fetch translations for many posts in one query, keyed by post_id. */
export async function fetchTranslations(
  postIds: string[],
  lang: Lang
): Promise<Map<string, BlogTranslation>> {
  const map = new Map<string, BlogTranslation>();
  if (lang === "en" || postIds.length === 0) return map;
  const { data } = await loose
    .from("blog_post_translations")
    .select("post_id, title, excerpt, content, meta_title, meta_description")
    .eq("locale", lang)
    .in("post_id", postIds);
  (data ?? []).forEach((t) => map.set(t.post_id, t));
  return map;
}

/** Fetch a single post's translation, or null. */
export async function fetchTranslation(
  postId: string,
  lang: Lang
): Promise<BlogTranslation | null> {
  if (lang === "en") return null;
  const { data } = await loose
    .from("blog_post_translations")
    .select("post_id, title, excerpt, content, meta_title, meta_description")
    .eq("locale", lang)
    .eq("post_id", postId)
    .maybeSingle();
  return data ?? null;
}

/** Merge a translation over a base record, ignoring empty translated fields. */
export function applyTranslation<T extends Record<string, unknown>>(
  base: T,
  tr: Partial<BlogTranslation> | null | undefined
): T {
  if (!tr) return base;
  const merged: Record<string, unknown> = { ...base };
  (["title", "excerpt", "content", "meta_title", "meta_description"] as const).forEach((k) => {
    const v = tr[k];
    if (typeof v === "string" && v.trim()) merged[k] = v;
  });
  return merged as T;
}
